import { Button, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { AssistantSubscriptionsDialog } from '../../../../../dialogs/AssistantSubscriptionsDialog'
import { useProfileStore } from '../../../../../store/storeProfile'
import { useWrapperStore } from '../../../../../store/storeWrapper'
import { theme } from '../../../../../theme'
import { BillingAddTable } from './BillingAddTable'
import { BillingSkeleton } from './BillingSkeleton'
import { BillingSpentTable } from './BillingSpentTable'
import { TypeFilter } from './TypeFilter'

export const Billing: React.FC = ({ isWebView = false }) => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const [openAssistantSubscriptionsDialog, setOpenAssistantSubscriptionsDialog] =
        useState<boolean>(false)
    const { fetchAssistantSubscriptions } = useWrapperStore((state) => state)
    const {
        fetchBillingListAdded,
        fetchBillingListSpent,
        billingListSpent,
        billingListAdded,
        isErrorFetchBillingList,
        isLoadingBillingList,
    } = useProfileStore((state) => state)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const [page, setPage] = React.useState(0)
    const [count, setCount] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(25)

    useEffect(() => {
        fetchAssistantSubscriptions()
    }, [])

    useEffect(() => {
        if (billingListSpent?.total_count) {
            setCount(billingListSpent.total_count)
        }
        if (billingListAdded?.total_count) {
            setCount(billingListAdded.total_count)
        }
    }, [billingListSpent, billingListAdded])

    useEffect(() => {
        if (searchParams.get('order_by') === 'add') {
            fetchBillingListAdded({
                limit: rowsPerPage,
                offset: page === 0 ? 0 : page * rowsPerPage,
            })
        } else {
            fetchBillingListSpent({
                limit: rowsPerPage,
                offset: page === 0 ? 0 : page * rowsPerPage,
            })
        }
    }, [searchParams, rowsPerPage, page])

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value)
    }
    const handleCloseAssistantSubscriptionsDialog = () => {
        setOpenAssistantSubscriptionsDialog(false)
    }

    const handleOpenAssistantSubscriptionsDialog = () => {
        setOpenAssistantSubscriptionsDialog(true)
    }

    return (
        <Grid size={12} sx={{ p: isMobile ? 0 : 3, pt: isMobile ? 2 : 3 }}>
            <Stack
                direction={isMobile ? 'column' : 'row'}
                sx={{ justifyContent: 'space-between' }}
                mb={2}>
                <Stack
                    direction={isMobile && !isWebView ? 'column' : 'row'}
                    sx={{ alignItems: 'center' }}
                    spacing={2}>
                    <Typography
                        variant={isWebView ? 'h5' : 'h4'}
                        sx={{ textAlign: 'left', color: '#000000', pl: isMobile ? 2 : 0 }}>
                        {t('finances.lastPayments')}
                    </Typography>
                    {isWebView && <TypeFilter setPage={setPage} />}
                    {!isWebView && (
                        <Button onClick={handleOpenAssistantSubscriptionsDialog} variant="outlined">
                            {t('finances.manageAssistantSubscriptions')}
                        </Button>
                    )}
                </Stack>

                {!isWebView && <TypeFilter setPage={setPage} />}
            </Stack>

            <Paper
                sx={{
                    p: isMobile ? 2 : 0,
                    border: '1px solid #E7E8EE',
                    width: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    borderRadius: '16px',
                }}>
                <TableContainer
                    sx={{
                        p: '0',
                        width: '100%',
                        overflowY: 'auto',
                    }}>
                    {searchParams.get('order_by') === 'add' ? (
                        <BillingAddTable />
                    ) : (
                        <BillingSpentTable />
                    )}

                    {(billingListSpent?.objects?.length === 0 ||
                        billingListAdded?.objects?.length === 0) &&
                        t('finances.noPaymentsYet')}

                    {isErrorFetchBillingList}
                    {isLoadingBillingList && <BillingSkeleton />}
                </TableContainer>
                <TablePagination
                    labelDisplayedRows={({ from, to, count }) =>
                        t('finances.displayedRows', { from, to, count: count !== -1 ? count : `>${to}` })
                    }
                    labelRowsPerPage={isMobile ? t('finances.total') : t('finances.totalRecords')}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-toolbar': {
                            flexWrap: 'wrap',
                            gap: 1,
                            justifyContent: 'flex-end',
                        },
                        '& .MuiTablePagination-spacer': {
                            flex: 'none',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: isMobile ? 'auto' : 0,
                        },
                        '& .MuiTablePagination-select': {
                            [theme.breakpoints.down('sm')]: {
                                display: 'none',
                            },
                        },
                        '& .MuiTablePagination-selectLabel': {
                            [theme.breakpoints.down('sm')]: {
                                display: 'none',
                            },
                        },
                    }}
                />
            </Paper>
            <AssistantSubscriptionsDialog
                open={openAssistantSubscriptionsDialog}
                handleClose={handleCloseAssistantSubscriptionsDialog}
            />
        </Grid>
    )
}
